import { useEffect, useState } from "react";
import { Link2, Search, TriangleAlert, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { JACE } from "./shared";

const EMPTY = { first_name: "", last_name: "", dob: "", email: "", phone: "", notes: "", member_email: "" };

// Gedeeld modal-formulier voor aanmaken én bewerken van een JACE-atleet.
// Toont duplicaat-waarschuwingen (naam+dob / exact e-mail / exact telefoon) met "toch opslaan".
export default function JaceAthleteForm({ athlete, onClose, onSaved }) {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [dups, setDups] = useState(null);
  const [memberQ, setMemberQ] = useState("");
  const [memberHits, setMemberHits] = useState([]);

  useEffect(() => {
    if (athlete) setF({ first_name: athlete.first_name || "", last_name: athlete.last_name || "", dob: athlete.dob || "", email: athlete.email || "", phone: athlete.phone || "", notes: athlete.notes || "", member_email: athlete.member_email || "" });
  }, [athlete]);

  const set = (k, v) => { setF((p) => ({ ...p, [k]: v })); setDups(null); };

  const searchMembers = async () => {
    if (memberQ.trim().length < 2) return;
    try {
      const { data } = await api.get(`/jace/athletes/member-search?q=${encodeURIComponent(memberQ.trim())}`);
      setMemberHits(data.items || []);
    } catch { toast.error("Leden zoeken mislukt"); }
  };

  const submit = async (allowDuplicate = false) => {
    if (!f.first_name.trim()) { toast.error("Voornaam is verplicht"); return; }
    setBusy(true);
    try {
      const payload = { ...f, allow_duplicate: allowDuplicate };
      const { data } = athlete
        ? await api.put(`/jace/athletes/${athlete.id}`, payload)
        : await api.post("/jace/athletes", payload);
      toast.success(athlete ? "Atleet bijgewerkt" : "Atleet aangemaakt");
      onSaved(data.athlete || data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail?.duplicates) setDups(detail.duplicates);
      else toast.error(typeof detail === "string" ? detail : "Opslaan mislukt");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/75 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#0A0A0A] border border-[#F5F3F0]/20 max-w-xl w-full p-6 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="jace-athlete-form">
        <div className="flex items-center justify-between mb-5">
          <div className="chip" style={{ color: JACE }}>// {athlete ? "ATLEET BEWERKEN" : "NIEUWE ATLEET"}</div>
          <button onClick={onClose} className="chip text-[#A3A3A3]" data-testid="jace-athlete-form-close"><X size={14}/></button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div><label className="chip text-[#A3A3A3]">Voornaam*</label><input value={f.first_name} onChange={(e) => set("first_name", e.target.value)} className="editorial-input dark w-full" data-testid="jace-athlete-first-name"/></div>
          <div><label className="chip text-[#A3A3A3]">Achternaam</label><input value={f.last_name} onChange={(e) => set("last_name", e.target.value)} className="editorial-input dark w-full" data-testid="jace-athlete-last-name"/></div>
          <div><label className="chip text-[#A3A3A3]">Geboortedatum</label><input type="date" value={f.dob} onChange={(e) => set("dob", e.target.value)} className="editorial-input dark w-full" data-testid="jace-athlete-dob"/></div>
          <div><label className="chip text-[#A3A3A3]">Telefoon</label><input value={f.phone} onChange={(e) => set("phone", e.target.value)} className="editorial-input dark w-full" data-testid="jace-athlete-phone"/></div>
          <div className="sm:col-span-2"><label className="chip text-[#A3A3A3]">E-mail</label><input type="email" value={f.email} onChange={(e) => set("email", e.target.value)} className="editorial-input dark w-full" data-testid="jace-athlete-email"/></div>
          <div className="sm:col-span-2"><label className="chip text-[#A3A3A3]">Notities (privé)</label><textarea value={f.notes} onChange={(e) => set("notes", e.target.value)} rows={3} className="editorial-input dark w-full" data-testid="jace-athlete-notes"/></div>

          <div className="sm:col-span-2 border border-[#F5F3F0]/10 p-4">
            <label className="chip text-[#A3A3A3] inline-flex items-center gap-1.5"><Link2 size={11}/> KOPPELING AAN WEBSITE-LID (OPTIONEEL)</label>
            {f.member_email ? (
              <div className="flex items-center justify-between mt-2">
                <span className="chip px-2 py-1 border" style={{ borderColor: JACE, color: JACE }} data-testid="jace-athlete-member-chip">{f.member_email}</span>
                <button onClick={() => set("member_email", "")} className="chip text-[#A3A3A3] hover:text-white" data-testid="jace-athlete-member-unlink">ONTKOPPEL</button>
              </div>
            ) : (
              <div className="mt-2">
                <div className="flex gap-2">
                  <input value={memberQ} onChange={(e) => setMemberQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && searchMembers()} placeholder="Zoek lid op naam of e-mail…" className="editorial-input dark w-full" data-testid="jace-athlete-member-search"/>
                  <button onClick={searchMembers} className="btn-ghost-dark inline-flex items-center gap-1.5" data-testid="jace-athlete-member-search-btn"><Search size={12}/></button>
                </div>
                {memberHits.length > 0 && (
                  <div className="mt-2 space-y-1" data-testid="jace-athlete-member-results">
                    {memberHits.map((m) => (
                      <button key={m.email} onClick={() => { set("member_email", m.email); setMemberHits([]); setMemberQ(""); }} className="w-full text-left chip px-3 py-2 border border-[#F5F3F0]/15 hover:border-[#C6FF3C] transition-colors" data-testid={`jace-athlete-member-pick-${m.email}`}>
                        {m.name || `${m.first_name || ""} ${m.last_name || ""}`.trim() || m.email} · {m.email}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {dups && (
          <div className="mt-5 border border-[#D4A93A] p-4" data-testid="jace-athlete-duplicates">
            <div className="chip inline-flex items-center gap-1.5" style={{ color: "#D4A93A" }}><TriangleAlert size={12}/> MOGELIJK DUPLICAAT — CONTROLEER EERST</div>
            <div className="mt-3 space-y-2">
              {dups.map((d) => (
                <div key={d.id} className="chip text-[#A3A3A3]" data-testid={`jace-athlete-dup-${d.id}`}>
                  <span className="text-[#F5F3F0]">{d.name}</span> · {d.reason}{d.dob ? ` · ${d.dob}` : ""}{d.email ? ` · ${d.email}` : ""}{d.phone ? ` · ${d.phone}` : ""}
                </div>
              ))}
            </div>
            <button onClick={() => submit(true)} disabled={busy} className="chip px-4 py-2 mt-4 border border-[#D4A93A] text-[#D4A93A]" data-testid="jace-athlete-save-anyway">TOCH OPSLAAN</button>
          </div>
        )}

        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="btn-ghost-dark" data-testid="jace-athlete-form-cancel">ANNULEREN</button>
          <button onClick={() => submit(false)} disabled={busy} className="chip px-5 py-2.5" style={{ background: JACE, color: "#0A0A0A" }} data-testid="jace-athlete-form-save">{busy ? "OPSlaan…" : "OPSLAAN"}</button>
        </div>
      </div>
    </div>
  );
}
